import "react-datepicker/dist/react-datepicker.css";
import "./date-field.web.css";

import {
    formatDisplayDate,
    formatIsoDate,
    parseIsoDate,
} from "@/lib/date";
import { cn } from "@/lib/utils";
import { forwardRef } from "react";
import { DatePicker } from "react-datepicker";
import { useTranslation } from "react-i18next";

type DateFieldProps = {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    minimumDate?: Date;
    maximumDate?: Date;
    className?: string;
    testID?: string;
    disabled?: boolean;
};

type DateTriggerProps = {
    value?: string;
    onClick?: () => void;
    displayLabel?: string;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    "data-testid"?: string;
};

const DateTrigger = forwardRef<HTMLButtonElement, DateTriggerProps>(
    (
        {
            onClick,
            displayLabel,
            placeholder,
            className,
            disabled = false,
            "data-testid": dataTestId,
        },
        ref,
    ) => {
        const label = displayLabel || placeholder;

        return (
            <button
                type="button"
                ref={ref}
                onClick={onClick}
                disabled={disabled}
                data-testid={dataTestId}
                aria-label={label}
                className={cn(
                    "border-input bg-background flex h-8 w-[140px] shrink-0 items-center justify-center overflow-hidden rounded-full border px-3 text-sm leading-5 shadow-sm shadow-black/5",
                    disabled
                        ? "cursor-not-allowed opacity-50"
                        : "transition-opacity hover:opacity-90",
                    displayLabel ? "text-foreground" : "text-muted-foreground",
                    className,
                )}
            >
                <span className="w-full truncate text-center">{label}</span>
            </button>
        );
    },
);

DateTrigger.displayName = "DateTrigger";

export function DateField({
    value,
    onChange,
    placeholder,
    minimumDate,
    maximumDate,
    className,
    testID,
    disabled = false,
}: DateFieldProps) {
    const selected = parseIsoDate(value) ?? null;
    const { t } = useTranslation();
    const tPlaceholder = placeholder ?? t("ui.selectDate");
    const displayLabel = value ? formatDisplayDate(value) : "";

    return (
        <div className="inline-flex w-auto max-w-max shrink-0">
            <DatePicker
                selected={selected}
                onChange={(date: Date | null) => {
                    if (date) {
                        onChange(formatIsoDate(date));
                    }
                }}
                disabled={disabled}
                minDate={minimumDate}
                maxDate={maximumDate}
                placeholderText={tPlaceholder}
                dateFormat="MMM d, yyyy"
                wrapperClassName="spork-datepicker-wrapper"
                customInput={
                    <DateTrigger
                        className={className}
                        displayLabel={displayLabel}
                        placeholder={tPlaceholder}
                        disabled={disabled}
                        data-testid={testID}
                    />
                }
                withPortal
                portalId="spork-date-picker-portal"
                shouldCloseOnSelect
                showPopperArrow={false}
                calendarClassName="spork-datepicker"
                popperClassName="spork-datepicker-popper"
            />
        </div>
    );
}
